import { useMemo, useState } from "react";
import { ArrowUpDown, Loader2, Inbox, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n, STATUS, PHASES } from "@/lib/i18n";

const STATUS_COLORS = {
  planned: "bg-white text-slate-600 border-slate-300 border-dashed",
  assigned: "bg-slate-100 text-slate-700 border-slate-300",
  scheduled: "bg-sky-50 text-sky-700 border-sky-300",
  in_progress: "bg-amber-50 text-amber-800 border-amber-300",
  submitted: "bg-indigo-50 text-indigo-700 border-indigo-300",
  in_review: "bg-purple-50 text-purple-700 border-purple-300",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
  reopened: "bg-rose-50 text-rose-700 border-rose-300",
  absent: "bg-zinc-100 text-zinc-600 border-zinc-300",
  refused: "bg-zinc-100 text-zinc-600 border-zinc-300",
  postponed: "bg-orange-50 text-orange-700 border-orange-300",
};

export const StatusBadge = ({ status }) => {
  const { lbl } = useI18n();
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${STATUS_COLORS[status] || ""}`}>
      {lbl(STATUS, status)}
    </span>
  );
};

export const PhaseBadge = ({ phase }) => {
  const { lbl } = useI18n();
  return <span className="inline-flex rounded bg-[#EAF3FA] text-[#153A63] px-2 py-0.5 text-xs font-medium">{lbl(PHASES, phase)}</span>;
};

export const PageHeader = ({ title, subtitle, actions, eyebrow }) => (
  <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-6 fade-up">
    <div>
      {eyebrow && <div className="label-xs mb-1">{eyebrow}</div>}
      <h1 data-testid="page-title" className="text-2xl sm:text-3xl font-bold tracking-tight text-[#153A63]">{title}</h1>
      {subtitle && <p className="text-sm text-slate-600 mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Loading = () => (
  <div data-testid="loading-state" className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center">
    <Loader2 className="h-4 w-4 animate-spin" /> …
  </div>
);

export const Empty = ({ text }) => (
  <div data-testid="empty-state" className="flex flex-col items-center gap-2 text-slate-500 text-sm py-10">
    <Inbox className="h-6 w-6 text-slate-400" /> {text}
  </div>
);

export const ErrorBox = ({ error }) => (
  <div data-testid="error-state" className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
    <AlertTriangle className="h-4 w-4" /> {error}
  </div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-1">
    <span className="label-xs">{label}</span>
    {children}
    {hint && <span className="block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const KPI = ({ label, value, sub, testid }) => (
  <div className="card-surface p-4" data-testid={testid}>
    <div className="label-xs">{label}</div>
    <div className="font-display text-2xl font-bold text-[#153A63] mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export const Bar = ({ pct, testid }) => (
  <div className="h-2 w-full rounded bg-slate-100 overflow-hidden" data-testid={testid}>
    <div className="h-full bg-[#2176AE] transition-[width] duration-500" style={{ width: `${Math.min(100, pct || 0)}%` }} />
  </div>
);

export function useSort(rows, initialKey, initialDir = "asc") {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState(initialDir);
  const sorted = useMemo(() => {
    const r = [...(rows || [])];
    if (!key) return r;
    r.sort((a, b) => {
      const x = a[key] ?? "";
      const y = b[key] ?? "";
      return (x > y ? 1 : x < y ? -1 : 0) * (dir === "asc" ? 1 : -1);
    });
    return r;
  }, [rows, key, dir]);
  const SortTh = ({ k, children }) => (
    <th aria-sort={key === k ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" data-testid={`sort-${k}`} className="inline-flex items-center gap-1 uppercase hover:text-[#153A63]"
        onClick={() => { if (key === k) setDir(dir === "asc" ? "desc" : "asc"); else { setKey(k); setDir("asc"); } }}>
        {children} <ArrowUpDown className="h-3 w-3" />
      </button>
    </th>
  );
  return { sorted, SortTh };
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, onConfirm, requireText, requireComment, destructive, testid = "confirm-dialog" }) {
  const { tx } = useI18n();
  const [text, setText] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = (!requireText || text === requireText) && (!requireComment || comment.trim());
  const go = async () => {
    setBusy(true);
    try { await onConfirm(comment); onOpenChange(false); setText(""); setComment(""); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="text-[#153A63]">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {requireText && (
          <Field label={<span className="normal-case">{tx("Saisir", "Type")} « <code data-testid={`${testid}-expected`} className="font-mono">{requireText}</code> » {tx("pour confirmer", "to confirm")}</span>}>
            <Input data-testid={`${testid}-text`} value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
        )}
        {requireComment && (
          <Field label={tx("Commentaire / motif (obligatoire)", "Comment / reason (required)")}>
            <Textarea data-testid={`${testid}-comment`} value={comment} onChange={(e) => setComment(e.target.value)} />
          </Field>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid={`${testid}-cancel`} onClick={() => onOpenChange(false)}>{tx("Annuler", "Cancel")}</Button>
          <Button data-testid={`${testid}-confirm`} disabled={!ok || busy} variant={destructive ? "destructive" : "default"} onClick={go}>
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{confirmLabel || tx("Confirmer", "Confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
